import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress } from '@mui/material';
import { DEFAULT_SEARCH_DEBOUNCE_MS } from './constants';
import KeyboardedInput from '../Input/KeyboardedInput';

const defaultTextFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: 32,
    alignItems: 'center',
    display: 'flex',
    borderRadius: 14,
    backgroundColor: 'rgba(38, 42, 47, 0.92)',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
    '& input': {
      padding: '6px 32px 6px 12px',
      margin: 0,
      color: '#f1f3f5',
      lineHeight: 1.2
    },
    '& fieldset, & .MuiOutlinedInput-notchedOutline': {
      top: 0,
      bottom: 0,
      borderRadius: 14,
      borderColor: 'rgba(255,255,255,0.16)'
    },
    '& legend': {
      display: 'none'
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255,255,255,0.28)'
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(255,255,255,0.42)',
      borderWidth: 1.5
    }
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 32px 6px 12px',
    color: '#f4f4f5'
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(255,255,255,0.58)',
    opacity: 1
  }
};

const defaultSpinnerSx = {
  color: 'rgba(255,255,255,0.72)',
  position: 'absolute',
  top: 9,
  right: 10,
  pointerEvents: 'none'
};

const DebouncedSearchInput = ({
  value,
  onDebouncedChange,
  placeholder = 'Search...',
  debounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
  showPendingIndicator = true,
  ariaLabel = 'Search',
  wrapperSx,
  textFieldSx,
  spinnerSx,
  inputProps,
  ...textFieldProps
}) => {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (inputValue === value) return undefined;
    const searchTimer = window.setTimeout(() => onDebouncedChange(inputValue), debounceMs);
    return () => window.clearTimeout(searchTimer);
  }, [debounceMs, inputValue, onDebouncedChange, value]);

  const isPending = inputValue !== value;

  return (
    <Box sx={{ position: 'relative', minWidth: 0, ...wrapperSx }}>
      <KeyboardedInput
        value={inputValue}
        onChange={setInputValue}
        placeholder={placeholder}
        size="small"
        variant="outlined"
        inputProps={{ 'aria-label': ariaLabel, ...inputProps }}
        sx={[defaultTextFieldSx, textFieldSx]}
        {...textFieldProps}
      />
      {showPendingIndicator && isPending && <CircularProgress size={14} thickness={5} sx={[defaultSpinnerSx, spinnerSx]} />}
    </Box>
  );
};

DebouncedSearchInput.propTypes = {
  value: PropTypes.string,
  onDebouncedChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  debounceMs: PropTypes.number,
  showPendingIndicator: PropTypes.bool,
  ariaLabel: PropTypes.string,
  wrapperSx: PropTypes.object,
  textFieldSx: PropTypes.object,
  spinnerSx: PropTypes.object,
  inputProps: PropTypes.object
};

DebouncedSearchInput.defaultProps = {
  value: '',
  placeholder: 'Search...',
  debounceMs: DEFAULT_SEARCH_DEBOUNCE_MS,
  showPendingIndicator: true,
  ariaLabel: 'Search',
  wrapperSx: undefined,
  textFieldSx: undefined,
  spinnerSx: undefined,
  inputProps: undefined
};

export default DebouncedSearchInput;
